import { useState, useMemo, useEffect, useCallback } from "react";
import * as api from "./Api";

const DEFAULT_LOW = 3;

// Normalize MongoDB _id to id for frontend consistency
const norm = (item) => ({ ...item, id: item._id || item.id });
const MANAGERS = { MG: "elizabeth.chua.yl@eurokarsev.com.sg", IM: "carolyn.theng.ml@eurokarsev.com.sg" };


// Shelter definitions: {cols, rows}
const SHELTER_LAYOUT = {
  "01": { cols: 5, rows: 3, label: "Shelter 01" },
  "02": { cols: 5, rows: 3, label: "Shelter 02" },
  "03": { cols: 7, rows: 3, label: "Shelter 03" },
  "04": { cols: 4, rows: 1, label: "Shelter 04 (Open)" },
};

const parsePos = (pos) => {
  if (!pos) return { shelter: "–", col: "–", row: "–" };
  const s = String(pos);
  if (s.length <= 2) return { shelter: s.padStart(2,"0"), col: "00", row: "00" };
  if (s.length <= 4) return { shelter: s.slice(0,2), col: s.slice(2).padStart(2,"0"), row: "00" };
  const p = s.padStart(6,"0");
  return { shelter: p.slice(0,2), col: p.slice(2,4), row: p.slice(4,6) };
};

const normalizePos = (pos) => {
  if (!pos) return "";
  const s = String(pos).replace(/[^0-9]/g, "");
  if (s.length === 0) return "";
  return s.padEnd(6, "0").slice(0, 6);
};

const CC = { MG: { bg:"rgba(212,68,59,0.12)", text:"#E85A5A", label:"MG" }, IM: { bg:"rgba(90,143,232,0.12)", text:"#5A8FE8", label:"IM" }, General: { bg:"rgba(204,153,153,0.12)", text:"#CC9999", label:"GEN" } };
const getCat = (cat) => CC[cat] || CC.General;
const StatusPill = ({qty, threshold}) => {
  if (qty === 0) return <span style={{fontSize:9,fontWeight:700,padding:"2px 6px",borderRadius:4,background:"rgba(255,68,68,0.15)",color:"#FF5544",letterSpacing:"0.04em"}}>OUT</span>;
  if (qty <= threshold) return <span style={{fontSize:9,fontWeight:700,padding:"2px 6px",borderRadius:4,background:"rgba(255,180,68,0.15)",color:"#FFB844",letterSpacing:"0.04em"}}>LOW</span>;
  return null;
};
const CatBadge = ({cat}) => { const c=getCat(cat); return <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:5,background:c.bg,color:c.text,display:"inline-flex",alignItems:"center",height:18,letterSpacing:"0.04em",lineHeight:1}}>{c.label}</span>; };

const font = "'DM Sans', sans-serif";
const mono = "'Space Mono', monospace";
const inputBase = { width:"100%", padding:"10px 14px", borderRadius:10, border:"1px solid rgba(255,200,200,0.12)", background:"rgba(255,255,255,0.04)", color:"#F5E6E8", fontSize:14, fontFamily:font, outline:"none", boxSizing:"border-box" };
const label = { display:"block", fontSize:11, fontWeight:600, color:"#AA8888", marginBottom:6, fontFamily:font, textTransform:"uppercase", letterSpacing:"0.06em" };
const btnP = { padding:"11px 24px", borderRadius:10, border:"none", background:"#D4443B", color:"#fff", fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:font, transition:"all 0.15s ease" };
const btnS = { ...btnP, background:"rgba(255,255,255,0.06)", color:"#CC9999" };
const btnDanger = { ...btnP, background:"rgba(255,68,68,0.12)", color:"#FF5544", border:"1px solid rgba(255,68,68,0.2)" };

const Modal = ({ isOpen, onClose, title, width, children }) => {
  if (!isOpen) return null;
  return (
    <div style={{ position:"fixed", inset:0, zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(20,0,0,0.75)", backdropFilter:"blur(8px)", animation:"fadeIn 0.15s ease" }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"#2A1014", borderRadius:16, padding:32, width: width||"min(500px,92vw)", maxHeight:"88vh", overflowY:"auto", border:"1px solid rgba(255,255,255,0.08)", boxShadow:"0 24px 80px rgba(0,0,0,0.6)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
          <h2 style={{ fontSize:20, fontWeight:700, color:"#F5E6E8", fontFamily:font, margin:0 }}>{title}</h2>
          <button onClick={onClose} type="button" aria-label="Close" style={{ background:"rgba(255,255,255,0.06)", border:"none", color:"#AA8888", width:32, height:32, borderRadius:8, cursor:"pointer", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
};

/* ── Shelter Grid Map Component ── */
const ShelterMap = ({ shelterCode, highlightCol, highlightRow, items }) => {
  const layout = SHELTER_LAYOUT[shelterCode];
  if (!layout) return <div style={{ fontSize:12, color:"#664444", padding:12 }}>Shelter {shelterCode}: layout not defined</div>;

  const { cols, rows } = layout;

  // Build occupancy map
  const cellItems = {};
  items.forEach(item => {
    const p = parsePos(item.position);
    if (p.shelter === shelterCode) {
      const key = `${p.col}-${p.row}`;
      if (!cellItems[key]) cellItems[key] = [];
      cellItems[key].push(item);
    }
  });

  const colNums = Array.from({ length: cols }, (_, i) => String(i + 1).padStart(2, "0"));
  const rowNums = Array.from({ length: rows }, (_, i) => String(i + 1).padStart(2, "0"));

  return (
    <div>
      <div style={{ fontSize:13, fontWeight:700, color:"#E8735A", fontFamily:mono, marginBottom:10 }}>
        📍 {layout.label}
      </div>
      <div style={{ overflowX:"auto" }}>
        <table style={{ borderCollapse:"collapse", width:"100%" }}>
          <thead>
            <tr>
              <th style={{ width:36, padding:6 }}></th>
              {colNums.map(c => (
                <th key={c} style={{
                  padding:"6px 4px", fontSize:11, fontWeight:700, color: c===highlightCol ? "#F5E6E8" : "#775555",
                  fontFamily:mono, textAlign:"center",
                  background: c===highlightCol ? "rgba(212,68,59,0.15)" : "transparent",
                  borderRadius:"6px 6px 0 0",
                }}>C{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowNums.map(r => (
              <tr key={r}>
                <td style={{
                  padding:"6px 8px", fontSize:11, fontWeight:700, fontFamily:mono, textAlign:"center",
                  color: r===highlightRow ? "#F5E6E8" : "#775555",
                  background: r===highlightRow ? "rgba(212,68,59,0.15)" : "transparent",
                  borderRadius:"6px 0 0 6px",
                }}>R{r}</td>
                {colNums.map(c => {
                  const isHighlight = c===highlightCol && r===highlightRow;
                  const key = `${c}-${r}`;
                  const cellCount = cellItems[key]?.length || 0;
                  const hasItems = cellCount > 0;
                  return (
                    <td key={c} style={{
                      padding:4, textAlign:"center",
                    }}>
                      <div style={{
                        minHeight: 44, borderRadius: 8, display:"flex", flexDirection:"column",
                        alignItems:"center", justifyContent:"center", gap:2, transition:"all 0.2s",
                        background: isHighlight
                          ? "linear-gradient(135deg, #D4443B, #E8735A)"
                          : hasItems
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(255,255,255,0.015)",
                        border: isHighlight
                          ? "2px solid #FF6B5A"
                          : hasItems
                            ? "1px solid rgba(255,100,100,0.1)"
                            : "1px solid rgba(255,100,100,0.04)",
                        boxShadow: isHighlight ? "0 0 16px rgba(212,68,59,0.4)" : "none",
                      }}>
                        {isHighlight && (
                          <span style={{ fontSize:14 }}>📌</span>
                        )}
                        {hasItems && !isHighlight && (
                          <span style={{ fontSize:10, color:"#886666", fontFamily:mono }}>{cellCount}</span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display:"flex", gap:12, marginTop:10, flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <div style={{ width:14, height:14, borderRadius:4, background:"linear-gradient(135deg, #D4443B, #E8735A)", border:"1px solid #FF6B5A" }}/>
          <span style={{ fontSize:10, color:"#AA8888" }}>This item</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <div style={{ width:14, height:14, borderRadius:4, background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,100,100,0.1)" }}/>
          <span style={{ fontSize:10, color:"#AA8888" }}>Has items</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <div style={{ width:14, height:14, borderRadius:4, background:"rgba(255,255,255,0.015)", border:"1px solid rgba(255,100,100,0.04)" }}/>
          <span style={{ fontSize:10, color:"#AA8888" }}>Empty</span>
        </div>
      </div>
    </div>
  );
};

const MG_LOGO = "data:image/png;base64,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";
const IM_LOGO = "data:image/png;base64,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";



export default function App() {
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [history, setHistory] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  const [tab, setTab] = useState("inventory");
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  // Auth
  const [loggedIn, setLoggedIn] = useState(api.isAuthenticated());
  const [showLogin, setShowLogin] = useState(false);
  const [alertExpanded, setAlertExpanded] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginError, setLoginError] = useState(false);

  // Fetch items and transactions from backend
  const refreshData = useCallback(async () => {
    try {
      setApiError("");
      const [itemsData, txData] = await Promise.all([
        api.fetchItems(),
        api.fetchTransactions(),
      ]);
      setItems(itemsData.map(norm));
      setMovements([...txData].sort((a,b) => new Date(b.date) - new Date(a.date)).map(t => ({
        id: t._id,
        dateKey: t.date ? new Date(t.date).toISOString().slice(0,10) : "",
        itemId: t.itemId,
        itemName: t.itemName,
        itemPos: t.position,
        type: t.type,
        qty: t.quantity,
        person: t.person,
        purpose: t.purpose || "–",
        datetime: t.date ? new Date(t.date).toLocaleString() : "",
        balanceBefore: t.previousQuantity,
        balanceAfter: t.newQuantity,
      })));
    } catch (e) {
      setApiError(e.message);
    }
  }, []);

  // Load data on mount and when logged in
  useEffect(() => {
    if (loggedIn) {
      setLoading(true);
      refreshData().finally(() => setLoading(false));
    }
  }, [loggedIn, refreshData]);

  const handleLogin = async () => {
    try {
      setLoginError(false);
      await api.login(loginEmail.trim().toLowerCase(), loginPass);
      setLoggedIn(true);
      setShowLogin(false);
    } catch (e) {
      setLoginError(true);
    }
  };
  const handleLogout = () => { api.logout(); setLoggedIn(false); setItems([]); setMovements([]); setTab("inventory"); };

  const [search, setSearch] = useState("");
  const [shelterFilter, setShelterFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [overviewCatFilter, setOverviewCatFilter] = useState("all");
  const [overviewSearch, setOverviewSearch] = useState("");
  const [sortBy, setSortBy] = useState("position");
  const [sortDir, setSortDir] = useState("asc");
  const [viewMode, setViewMode] = useState("table");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({ name:"", quantity:"", unit:"pcs", position:"", notes:"", category:"General", lowThreshold:String(DEFAULT_LOW) });

  const [moveModal, setMoveModal] = useState(false);
  const [moveType, setMoveType] = useState("out");
  const [moveForm, setMoveForm] = useState({ itemId:"", qty:"", person:"", purpose:"" });
  const [moveSearch, setMoveSearch] = useState("");
  const [moveHistoryFilter, setMoveHistoryFilter] = useState("all");

  // Item detail panel
  const [selectedItemId, setSelectedItemId] = useState(null);

  // Inline edit with security code
  const [editMode, setEditMode] = useState(false);
  const [securityInput, setSecurityInput] = useState("");
  const [securityError, setSecurityError] = useState(false);
  const [securityPassed, setSecurityPassed] = useState(false);
  const [inlineEdit, setInlineEdit] = useState({ name:"", quantity:"", category:"General", lowThreshold:"3", position:"" });
  const [page, setPage] = useState(1);
  const [mvPage, setMvPage] = useState(1);
  const [hPage, setHPage] = useState(1);
  const [ovPage, setOvPage] = useState(1);
  // Persist history and dismissedAlerts to localStorage (frontend-only features)
  useEffect(() => {
    try { localStorage.setItem("stock-tracker-hist", JSON.stringify({ history, dismissedAlerts })); } catch(e) { /* ignore */ }
  }, [history, dismissedAlerts]);
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("stock-tracker-hist"));
      if (saved?.history) setHistory(saved.history);
      if (saved?.dismissedAlerts) setDismissedAlerts(saved.dismissedAlerts);
    } catch(e) { /* ignore */ }
  }, []);

  const EDIT_CODE = "2600";

  // Derived selected item (always in sync — no stale state)
  const liveSelectedItem = useMemo(() => selectedItemId ? items.find(i=>i.id===selectedItemId) || null : null, [items, selectedItemId]);
  const selectedPos = liveSelectedItem ? parsePos(liveSelectedItem.position) : null;
  const itemMovements = useMemo(() => selectedItemId ? movements.filter(m=>m.itemId===selectedItemId) : [], [movements, selectedItemId]);
  useEffect(() => setPage(1), [search, shelterFilter, stockFilter, catFilter, sortBy, sortDir]);
  useEffect(() => setMvPage(1), [moveSearch, moveHistoryFilter]);
  useEffect(() => setOvPage(1), [overviewCatFilter, overviewSearch]);
  

  const handleSecuritySubmit = () => {
    if (securityInput === EDIT_CODE) {
      setSecurityPassed(true);
      setSecurityError(false);
      if (liveSelectedItem) setInlineEdit({ name:liveSelectedItem.name, quantity:String(liveSelectedItem.quantity), category:liveSelectedItem.category||"General", lowThreshold:String(liveSelectedItem.lowThreshold??DEFAULT_LOW), position:liveSelectedItem.position||"" });
    } else {
      setSecurityError(true);
      setSecurityPassed(false);
    }
  };

  const handleInlineSave = async () => {
    if (!inlineEdit.name.trim() || inlineEdit.quantity==="") return;
    const item = liveSelectedItem;
    if (!item) return;
    const ch = [];
    if (item.name!==inlineEdit.name) ch.push(`name: "${item.name}" → "${inlineEdit.name}"`);
    if (item.quantity!==Number(inlineEdit.quantity)) ch.push(`qty: ${item.quantity} → ${inlineEdit.quantity}`);
    if (item.category!==(inlineEdit.category||"General")) ch.push(`cat: ${item.category} → ${inlineEdit.category}`);
    if (item.lowThreshold!==Number(inlineEdit.lowThreshold||DEFAULT_LOW)) ch.push(`threshold: ${item.lowThreshold} → ${inlineEdit.lowThreshold}`);
    if (item.position!==normalizePos(inlineEdit.position)) ch.push(`pos: ${item.position} → ${normalizePos(inlineEdit.position)}`);
    if (ch.length) {
      try {
        await api.updateItem(item.id, { name:inlineEdit.name, quantity:Number(inlineEdit.quantity), category:inlineEdit.category||"General", lowThreshold:Number(inlineEdit.lowThreshold||DEFAULT_LOW), position:normalizePos(inlineEdit.position) });
        addHist("Edited", inlineEdit.name, ch.join("; "));
        await refreshData();
      } catch(e) { setApiError(e.message); }
    }
    setEditMode(false); setSecurityPassed(false); setSecurityInput("");
  };

  const cancelInlineEdit = () => {
    setEditMode(false); setSecurityPassed(false); setSecurityInput(""); setSecurityError(false);
  };

  const resetForm = () => setForm({ name:"", quantity:"", unit:"pcs", position:"", notes:"", category:"General", lowThreshold:String(DEFAULT_LOW) });
  const addHist = (action, itemName, details) => {
    setHistory(h => [{ id:crypto.randomUUID(), action, itemName, details, datetime:new Date().toLocaleString() }, ...h]);
  };
  const shelters = useMemo(() => [...new Set(items.map(i => parsePos(i.position).shelter))].sort(), [items]);

  const openAdd = () => { resetForm(); setFormError(""); setEditingItem(null); setModalOpen(true); };
  const openEdit = (item) => { setFormError("");
    setForm({ name:item.name, quantity:String(item.quantity), unit:item.unit, position:item.position, notes:item.notes, category:item.category||"General", lowThreshold:String(item.lowThreshold||DEFAULT_LOW) });
    setEditingItem(item); setModalOpen(true);
  };
  const [formError, setFormError] = useState("");
  const handleSave = async () => {
    if (!form.name.trim()) { setFormError("Item name is required"); return; }
    if (form.quantity==="" || Number(form.quantity)<0) { setFormError("Quantity must be ≥ 0"); return; }
    if (form.position && normalizePos(form.position).length!==6) { setFormError("Position must be 6 digits (SSCCRR)"); return; }
    setFormError("");
    try {
      if (editingItem) {
        const old = editingItem;
        await api.updateItem(old.id, { name:form.name, quantity:Number(form.quantity), unit:form.unit, lowThreshold:Number(form.lowThreshold||DEFAULT_LOW), position:normalizePos(form.position), notes:form.notes, category:form.category||"General" });
        const ch = [];
        if (old.name!==form.name) ch.push(`name: "${old.name}" → "${form.name}"`);
        if (old.quantity!==Number(form.quantity)) ch.push(`qty: ${old.quantity} → ${form.quantity}`);
        if (old.position!==normalizePos(form.position)) ch.push(`pos: ${old.position} → ${normalizePos(form.position)}`);
        if (old.unit!==form.unit) ch.push(`unit: ${old.unit} → ${form.unit}`);
        if (old.notes!==form.notes) ch.push(`notes updated`);
        if (ch.length) addHist("Edited", form.name, ch.join("; "));
      } else {
        await api.createItem({ name:form.name, quantity:Number(form.quantity), unit:form.unit, lowThreshold:Number(form.lowThreshold||DEFAULT_LOW), position:normalizePos(form.position), notes:form.notes, category:form.category||"General" });
        addHist("Added", form.name, `Qty: ${form.quantity} ${form.unit}, Pos: ${form.position}`);
      }
      await refreshData();
    } catch(e) { setFormError(e.message); return; }
    setModalOpen(false); resetForm(); setEditingItem(null);
  };
  const handleDelete = async (id) => {
    const item = items.find(i=>i.id===id);
    try {
      await api.deleteItem(id);
      if (item) addHist("Deleted", item.name, `Had ${item.quantity} ${item.unit} at ${item.position}`);
      await refreshData();
    } catch(e) { setApiError(e.message); }
    setDeleteConfirm(null);
    if (selectedItemId===id) setSelectedItemId(null);
  };

  const openMove = (type, item) => {
    setMoveType(type);
    setMoveForm({ itemId:item?item.id:"", qty:"", person:"", purpose:"" });
    setMoveError(""); setMoveModal(true);
  };
  const [moveError, setMoveError] = useState("");
  const handleMove = async () => {
    const itemId = moveForm.itemId;
    const qty = Number(moveForm.qty);
    if (!itemId) { setMoveError("Select an item"); return; }
    if (!qty || qty<=0 || !Number.isInteger(qty)) { setMoveError("Quantity must be a whole number ≥ 1"); return; }
    if (!moveForm.person.trim() || moveForm.person.trim().length<2) { setMoveError("Person name required (min 2 chars)"); return; }
    setMoveError("");
    const item = items.find(i=>i.id===itemId);
    if (!item) return;
    if (moveType==="out" && qty>item.quantity) { setMoveError("Insufficient stock"); return; }
    try {
      await api.moveStock(itemId, moveType, qty, moveForm.person.trim(), moveForm.purpose.trim()||"");
      await refreshData();
    } catch(e) { setMoveError(e.message); return; }
    setMoveModal(false);
  };

  const csvEsc = (v) => `"${String(v??"").replace(/"/g,'""')}"`;
  const exportCSV = () => {
    const h = ["Name","Quantity","Unit","Position","Shelter","Column","Row","Last Checked","Notes"];
    const rows = filteredItems.map(i => { const p=parsePos(i.position); return [csvEsc(i.name),i.quantity,i.unit,i.position,p.shelter,p.col,p.row,i.lastChecked,csvEsc(i.notes)]; });
    const csv = [h.join(","),...rows.map(r=>r.join(","))].join("\n");
    const b=new Blob([csv],{type:"text/csv"}); const u=URL.createObjectURL(b);
    const a=document.createElement("a"); a.href=u; a.download=`inventory_${new Date().toISOString().slice(0,10)}.csv`; a.click(); URL.revokeObjectURL(u);
  };
  const exportMovements = () => {
    const h = ["Date/Time","Type","Item","Position","Qty","Person","Purpose","Before","After"];
    const rows = filteredMovements.map(m=>[csvEsc(m.datetime),m.type.toUpperCase(),csvEsc(m.itemName),m.itemPos,m.qty,csvEsc(m.person),csvEsc(m.purpose),m.balanceBefore,m.balanceAfter]);
    const csv = [h.join(","),...rows.map(r=>r.join(","))].join("\n");
    const b=new Blob([csv],{type:"text/csv"}); const u=URL.createObjectURL(b);
    const a=document.createElement("a"); a.href=u; a.download=`movements_${new Date().toISOString().slice(0,10)}.csv`; a.click(); URL.revokeObjectURL(u);
  };

  const filteredItems = useMemo(() => {
    const q=search.toLowerCase();
    return items.filter(i => {
      const ms=i.name.toLowerCase().includes(q)||i.position.includes(q)||i.notes.toLowerCase().includes(q);
      const msh=shelterFilter==="all"||parsePos(i.position).shelter===shelterFilter;
      const mst=stockFilter==="all"||(stockFilter==="low"&&i.quantity<=i.lowThreshold)||(stockFilter==="zero"&&i.quantity===0)||(stockFilter==="ok"&&i.quantity>i.lowThreshold);
      const mc=catFilter==="all"||i.category===catFilter;
      return ms&&msh&&mst&&mc;
    }).sort((a,b) => {
      let c=0;
      if (sortBy==="name") c=a.name.localeCompare(b.name);
      else if (sortBy==="quantity") c=a.quantity-b.quantity;
      else if (sortBy==="position") c=a.position.localeCompare(b.position);
      return sortDir==="asc"?c:-c;
    });
  }, [items, search, shelterFilter, stockFilter, catFilter, sortBy, sortDir]);
  const filteredMovements = useMemo(() => movements.filter(m => {
    if (moveHistoryFilter!=="all" && m.type!==moveHistoryFilter) return false;
    if (!moveSearch) return true;
    const q=moveSearch.toLowerCase();
    return m.itemName.toLowerCase().includes(q)||m.person.toLowerCase().includes(q)||m.purpose.toLowerCase().includes(q);
  }), [movements, moveHistoryFilter, moveSearch]);
  const groupedByShelter = useMemo(() => {
    const g={}; filteredItems.forEach(item => { const s=parsePos(item.position).shelter; if(!g[s])g[s]=[]; g[s].push(item); }); return g;
  }, [filteredItems]);

  const PAGE_SIZE = 20;
  const paginatedItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const paginatedMv = filteredMovements.slice((mvPage-1)*PAGE_SIZE, mvPage*PAGE_SIZE);
  const paginatedHist = history.slice((hPage-1)*PAGE_SIZE, hPage*PAGE_SIZE);
  const Pager = ({p, setP, total}) => { const tp=Math.ceil(total/PAGE_SIZE); return tp>1?(<div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:4, padding:"14px 0" }}><button onClick={()=>setP(1)} disabled={p===1} style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,100,100,0.08)", color:p===1?"#553333":"#CC9999", borderRadius:6, padding:"4px 8px", fontSize:11, cursor:p===1?"default":"pointer", fontFamily:font }}>«</button><button onClick={()=>setP(v=>Math.max(1,v-1))} disabled={p===1} style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,100,100,0.08)", color:p===1?"#553333":"#CC9999", borderRadius:6, padding:"4px 10px", fontSize:11, cursor:p===1?"default":"pointer", fontFamily:font }}>‹</button><span style={{ fontSize:11, color:"#AA8888", padding:"0 8px", fontFamily:mono }}>{p} / {tp}</span><button onClick={()=>setP(v=>Math.min(tp,v+1))} disabled={p===tp} style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,100,100,0.08)", color:p===tp?"#553333":"#CC9999", borderRadius:6, padding:"4px 10px", fontSize:11, cursor:p===tp?"default":"pointer", fontFamily:font }}>›</button><button onClick={()=>setP(tp)} disabled={p===tp} style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,100,100,0.08)", color:p===tp?"#553333":"#CC9999", borderRadius:6, padding:"4px 8px", fontSize:11, cursor:p===tp?"default":"pointer", fontFamily:font }}>»</button></div>):null; };

  const totalItems=items.length;
  const lowStockItems=useMemo(()=>items.filter(i=>i.quantity<=i.lowThreshold),[items]);
  const visibleLowItems=useMemo(()=>lowStockItems.filter(i=>!dismissedAlerts.includes(i.id)),[lowStockItems,dismissedAlerts]);
  const lowStockCount=lowStockItems.length;
  const totalQty=useMemo(()=>items.reduce((s,i)=>s+i.quantity,0),[items]);
  const todayKey=new Date().toISOString().slice(0,10);
  const todayMoves=useMemo(()=>movements.filter(m=>(m.dateKey||"")===todayKey).length,[movements,todayKey]);

  const handleSort=(col)=>{if(sortBy===col)setSortDir(d=>d==="asc"?"desc":"asc");else{setSortBy(col);setSortDir("asc");}};
  const SortIcon=({col})=><span style={{marginLeft:4,opacity:sortBy===col?1:0.3,fontSize:10}}>{sortBy===col&&sortDir==="desc"?"▼":"▲"}</span>;

  // Item detail data



  // Overview consolidated
  const overview = useMemo(()=>{const map={};items.forEach(i=>{const key=`${i.name}__${i.unit}__${i.category||"General"}`;if(!map[key])map[key]={name:i.name,totalQty:0,unit:i.unit,category:i.category||"General",positions:[],lowThreshold:i.lowThreshold||DEFAULT_LOW};map[key].totalQty+=i.quantity;map[key].positions.push(i.position);});return Object.values(map).sort((a,b)=>a.name.localeCompare(b.name));},[items]);
  const filteredOverview = overview.filter(oi=>{const mc=overviewCatFilter==="all"||oi.category===overviewCatFilter;const ms=!overviewSearch||oi.name.toLowerCase().includes(overviewSearch.toLowerCase());return mc&&ms;});
  const paginatedOv = filteredOverview.slice((ovPage-1)*PAGE_SIZE, ovPage*PAGE_SIZE);
  const lowMG=visibleLowItems.filter(i=>i.category==="MG");const lowIM=visibleLowItems.filter(i=>i.category==="IM");const lowGen=visibleLowItems.filter(i=>i.category==="General");
  const buildMailto=(to,subj,body)=>`mailto:${to}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body)}`;
  const buildAlertEmail=(cat)=>{const list=cat==="MG"?lowMG:cat==="IM"?lowIM:lowGen;const to=cat==="MG"?MANAGERS.MG:cat==="IM"?MANAGERS.IM:`${MANAGERS.MG},${MANAGERS.IM}`;const body=`Hi,\n\nThe following ${cat} items are running low:\n\n${list.map(i=>`• ${i.name} — ${i.quantity} ${i.unit} (threshold: ${i.lowThreshold}) at ${i.position}`).join("\n")}\n\nPlease arrange restocking.\n\nBest regards,\nMarketing Team`;return buildMailto(to,`[Low Stock Alert] ${cat} Items Need Restocking`,body);};
  const tabs=loggedIn?[{id:"inventory",label:"📦 Inventory"},{id:"movements",label:"🔄 Movements"},{id:"history",label:"📜 History"},{id:"overview",label:"📊 Overview"}]:[];

  return (<>
    <style>{`
      :root{--bg:#1A0508;--panel:rgba(255,255,255,0.02);--border:rgba(255,100,100,0.08);--border-light:rgba(255,100,100,0.04);--text:#F5E6E8;--muted:#885555;--accent:#E8735A;--danger:#D4443B;--success:#2E7D4A;--card-radius:14px}
      @keyframes fadeIn{from{opacity:0;transform:scale(0.97)}to{opacity:1;transform:scale(1)}}
      @keyframes slideIn{from{opacity:0;transform:translateX(8px)}to{opacity:1;transform:translateX(0)}}
      table tr:hover td{background:rgba(255,255,255,0.015)} thead tr:hover td{background:transparent}
      button:active{transform:scale(0.98)}
      input:focus,select:focus{border-color:rgba(232,115,90,0.3)!important;box-shadow:0 0 0 2px rgba(232,115,90,0.1)}
    `}</style>
    <div style={{ minHeight:"100vh", background:"linear-gradient(160deg, #1A0508 0%, #2D0A0F 40%, #1F0709 100%)", color:"#F5E6E8", fontFamily:font }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap" rel="stylesheet" />

      {/* Header + Tabs (one sticky block) */}
      <div style={{ position:"sticky", top:0, zIndex:101, background:"#1A0508", borderBottom:loggedIn?"1px solid rgba(255,100,100,0.06)":"1px solid rgba(255,100,100,0.08)" }}>
        {/* Header row */}
        <div style={{ padding:"16px 28px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12, maxWidth:1280, margin:"0 auto" }}>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <img src={MG_LOGO} alt="MG" style={{ height:36, width:36, borderRadius:8, objectFit:"contain" }}/>
              <div style={{ width:1, height:28, background:"rgba(255,100,100,0.2)" }}/>
              <img src={IM_LOGO} alt="IM Motors" style={{ height:30, objectFit:"contain", filter:"brightness(1.2)" }}/>
            </div>
            <div>
              <h1 style={{ fontSize:20, fontWeight:700, margin:0, fontFamily:mono, letterSpacing:"-0.02em", color:"#F5E6E8" }}>STOCK TRACKER</h1>
              <p style={{ fontSize:11, color:"#885555", margin:"2px 0 0" }}>Marketing Inventory Management</p>
            </div>
          </div>
          {loggedIn && <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
            <button onClick={()=>openMove("out",null)} style={{ ...btnP, background:"#B83A33", fontSize:13, padding:"9px 18px" }}>↑ Take Out</button>
            <button onClick={()=>openMove("in",null)} style={{ ...btnP, background:"#2E7D4A", fontSize:13, padding:"9px 18px" }}>↓ Stock In</button>
            <button onClick={handleLogout} style={{ ...btnS, padding:"9px 18px", fontSize:13 }}>🚪 Logout</button>
          </div>}
        </div>
        {/* Tabs row */}
        {loggedIn && (
          <div style={{ borderTop:"1px solid rgba(255,100,100,0.06)" }}>
            <div style={{ padding:"0 28px", display:"flex", overflowX:"auto", maxWidth:1280, margin:"0 auto" }}>
              {tabs.map(t=>(
                <button key={t.id} onClick={()=>setTab(t.id)} style={{
                  padding:"14px 20px", border:"none", cursor:"pointer", fontFamily:font, fontSize:13,
                  fontWeight:tab===t.id?700:500, color:tab===t.id?"#E8735A":"#775555", background:"transparent",
                  borderBottom:tab===t.id?"2px solid #E8735A":"2px solid transparent", transition:"all 0.2s", whiteSpace:"nowrap",
                }}>{t.label}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Loading / Error */}
      {loading && (
        <div style={{ textAlign:"center", padding:"60px 28px", color:"#AA8888", fontSize:16 }}>Loading inventory...</div>
      )}
      {apiError && (
        <div style={{ margin:"16px 28px", padding:"12px 16px", background:"rgba(255,68,68,0.08)", borderRadius:10, border:"1px solid rgba(255,68,68,0.2)", color:"#FF5544", fontSize:13 }}>
          Error: {apiError} <button onClick={()=>setApiError("")} style={{ background:"transparent", border:"none", color:"#FF5544", cursor:"pointer", marginLeft:8 }}>✕</button>
        </div>
      )}

      {/* Guest Landing */}
      {!loggedIn && (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"80px 28px 60px", textAlign:"center", maxWidth:1280, margin:"0 auto" }}>
          <div style={{ background:"rgba(255,255,255,0.02)", borderRadius:20, padding:"48px 40px 44px", border:"1px solid rgba(255,100,100,0.08)", maxWidth:480, width:"100%", boxShadow:"0 8px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize:52, marginBottom:12 }}>📦</div>
            <h2 style={{ fontSize:24, fontWeight:700, color:"#F5E6E8", margin:"0 0 8px", fontFamily:font }}>Marketing Inventory</h2>
            <p style={{ fontSize:14, color:"#886666", marginBottom:36, lineHeight:1.5 }}>Log in to manage inventory, take out or stock in items.</p>
            <div style={{ borderTop:"1px solid rgba(255,100,100,0.08)", paddingTop:18 }}>
              <button onClick={()=>setShowLogin(true)} style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,100,100,0.12)", color:"#CC9999", fontSize:14, padding:"12px 36px", borderRadius:11, cursor:"pointer", fontFamily:font, fontWeight:600, transition:"all 0.2s" }}>🔒 Admin Login</button>
            </div>
          </div>
        </div>
      )}



      <div style={{ maxWidth:1280, margin:"0 auto" }}>
      {/* ═══ INVENTORY TAB ═══ */}
      {tab==="inventory" && loggedIn && (<>
        {/* Stats */}
        {loggedIn && <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(145px, 1fr))", gap:10, padding:"18px 28px" }}>
          {[
            { l:"Total Entries", v:totalItems, a:"#E8735A" },
            { l:"Total Stock", v:totalQty.toLocaleString(), a:"#CC9999" },
            { l:"Low / Out", v:lowStockCount, a:lowStockCount>0?"#FF5544":"#66AA77" },
            { l:"Moves Today", v:todayMoves, a:"#D4886A" },
          ].map((s,i) => (
            <div key={i} style={{ background:"rgba(255,255,255,0.025)", borderRadius:12, padding:"14px 16px", border:"1px solid rgba(255,100,100,0.06)" }}>
              <div style={{ fontSize:10, color:"#885555", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>{s.l}</div>
              <div style={{ fontSize:24, fontWeight:700, color:s.a, fontFamily:mono }}>{s.v}</div>
            </div>
          ))}
        </div>}


        {/* Low Stock Alert */}
        {loggedIn && visibleLowItems.length > 0 && (
          <div style={{ margin:"0 28px 16px", padding:"12px 16px", background:"rgba(255,68,68,0.06)", borderRadius:12, border:"1px solid rgba(255,68,68,0.15)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10, flexWrap:"wrap", gap:8 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                <span style={{ fontSize:13, fontWeight:700, color:"#FF5544" }}>⚠️ Low Stock — {visibleLowItems.length} item(s)</span>
                {dismissedAlerts.length>0&&<span style={{ fontSize:10, color:"#886666" }}>({dismissedAlerts.length} hidden)</span>}
                {dismissedAlerts.length>0&&<button onClick={()=>{addHist("Alerts Reset","All Items","Restored "+dismissedAlerts.length+" dismissed alert(s)");setDismissedAlerts([]);}} style={{ fontSize:10, color:"#886666", background:"transparent", border:"none", cursor:"pointer", fontFamily:font, textDecoration:"underline" }}>Reset</button>}
              </div>
              <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                {lowMG.length>0&&<a href={buildAlertEmail("MG")} style={{ fontSize:10, padding:"4px 10px", borderRadius:6, background:"rgba(212,68,59,0.15)", color:"#E85A5A", textDecoration:"none", fontWeight:600 }}>📧 MG ({lowMG.length})</a>}
                {lowIM.length>0&&<a href={buildAlertEmail("IM")} style={{ fontSize:10, padding:"4px 10px", borderRadius:6, background:"rgba(90,143,232,0.15)", color:"#5A8FE8", textDecoration:"none", fontWeight:600 }}>📧 IM ({lowIM.length})</a>}
                {lowGen.length>0&&<a href={buildAlertEmail("General")} style={{ fontSize:10, padding:"4px 10px", borderRadius:6, background:"rgba(204,153,153,0.15)", color:"#CC9999", textDecoration:"none", fontWeight:600 }}>📧 Both ({lowGen.length})</a>}
              </div>
            </div>
            <div style={{ display:"flex", gap:5, flexWrap:"wrap", overflow:"hidden", maxHeight:alertExpanded?"none":"30px", position:"relative" }}>{visibleLowItems.map(i=><span key={i.id} style={{ fontSize:10, padding:"3px 8px", borderRadius:7, background:"rgba(255,68,68,0.1)", color:"#FF8877", display:"flex", alignItems:"center", gap:3, whiteSpace:"nowrap" }}><CatBadge cat={i.category}/>{i.name} ({i.quantity})<button onClick={(e)=>{e.stopPropagation();setDismissedAlerts(d=>[...d,i.id]);addHist("Alert Dismissed",i.name,"Dismissed low stock alert (qty: "+i.quantity+" "+i.unit+")");}} style={{ background:"transparent", border:"none", color:"#664444", cursor:"pointer", fontSize:9, padding:"0 1px", lineHeight:1 }}>✕</button></span>)}</div>
            {visibleLowItems.length>5&&<div style={{ marginTop:6 }}>{!alertExpanded?<button onClick={()=>setAlertExpanded(true)} style={{ fontSize:10, color:"#E8735A", background:"rgba(232,115,90,0.1)", border:"none", cursor:"pointer", padding:"3px 10px", borderRadius:7, fontWeight:600, fontFamily:font }}>Show all {visibleLowItems.length} items ↓</button>:<button onClick={()=>setAlertExpanded(false)} style={{ fontSize:10, color:"#886666", background:"rgba(255,255,255,0.04)", border:"none", cursor:"pointer", padding:"3px 10px", borderRadius:7, fontFamily:font }}>Show less ↑</button>}</div>}
          </div>
        )}
        {/* Position Legend */}
        <div style={{ padding:"0 28px 6px", display:"flex", gap:12, alignItems:"center", flexWrap:"wrap" }}>
          <span style={{ fontSize:11, color:"#775555", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.06em" }}>Position Code:</span>
          {["Shelter","Column","Row"].map((l,i) => (
            <span key={i} style={{ display:"flex", alignItems:"center", gap:4 }}>
              <span style={{ padding:"2px 8px", borderRadius:6, background:["rgba(212,68,59,0.2)","rgba(232,115,90,0.2)","rgba(204,153,153,0.2)"][i], color:["#E85A5A","#E8935A","#CC9999"][i], fontSize:11, fontWeight:600, fontFamily:mono }}>XX</span>
              <span style={{ fontSize:11, color:"#775555" }}>{l}</span>
              {i<2 && <span style={{ color:"#553333", margin:"0 2px" }}>–</span>}
            </span>
          ))}
          <span style={{ fontSize:11, color:"#664444", fontStyle:"italic" }}>Click any item name to see its position map</span>
        </div>

        {/* Filters */}
        <div style={{ padding:"8px 28px 16px", display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          <div style={{ position:"relative", flex:"1 1 200px", maxWidth:300 }}>
            <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color:"#664444", fontSize:14 }}>🔍</span>
            <input placeholder="Search name, position..." value={search} onChange={e=>setSearch(e.target.value)} style={{ ...inputBase, paddingLeft:34 }} />
          </div>
          <select value={shelterFilter} onChange={e=>setShelterFilter(e.target.value)} style={{ ...inputBase, width:"auto", minWidth:130, cursor:"pointer" }}>
            <option value="all">All Shelters</option>
            {shelters.map(s=><option key={s} value={s}>Shelter {s}</option>)}
          </select>
          <select value={stockFilter} onChange={e=>setStockFilter(e.target.value)} style={{ ...inputBase, width:"auto", minWidth:130, cursor:"pointer" }}>
            <option value="all">All Stock</option>
            <option value="low">⚠️ Low Stock</option>
            <option value="zero">🚫 Out</option>
            <option value="ok">✅ In Stock</option>
          </select>
          <button onClick={()=>setViewMode(v=>v==="table"?"grid":"table")} style={{ ...btnS, padding:"10px 14px", fontSize:13 }}>
            {viewMode==="table"?"🗂 Grid":"📋 Table"}
          </button>
          <button onClick={exportCSV} style={{ ...btnS, padding:"10px 14px", fontSize:13 }}>↓ CSV</button>
          <button onClick={openAdd} style={{ ...btnP, padding:"10px 14px", fontSize:13 }}>+ Add</button>
        </div>

        {/* Main content area: table/grid + detail panel */}
        <div style={{ display:"flex", gap:0, padding:"0 28px 32px", alignItems:"flex-start" }}>
          {/* Left: Table or Grid */}
          <div style={{ flex:1, minWidth:0, overflowX:"auto", transition:"all 0.3s" }}>
            {viewMode==="table" && (
              <>
                <div style={{ background:"rgba(255,255,255,0.015)", borderRadius:14, border:"1px solid rgba(255,100,100,0.06)", overflow:"hidden" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", minWidth:680 }}>
                    <thead>
                      <tr style={{ borderBottom:"1px solid rgba(255,100,100,0.08)" }}>
                        {[
                          {col:"name",l:"Item Name",w:"26%"},{col:"quantity",l:"Qty",w:"9%"},{col:null,l:"Unit",w:"7%"},
                          {col:"position",l:"Position",w:"12%"},
                          {col:null,l:"Quick Move",w:"17%"},{col:null,l:"",w:"7%"},
                        ].map((h,i)=>(
                          <th key={i} onClick={()=>h.col&&handleSort(h.col)} style={{
                            textAlign:"left", padding:"12px 12px", fontSize:10, fontWeight:600, color:"#775555",
                            textTransform:"uppercase", letterSpacing:"0.08em", width:h.w,
                            cursor:h.col?"pointer":"default", userSelect:"none",
                          }}>{h.l}{h.col&&<SortIcon col={h.col}/>}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.length===0 ? (
                        <tr><td colSpan={5} style={{ textAlign:"center", padding:48, color:"#664444" }}>No items found.</td></tr>
                      ) : paginatedItems.map(item => {
                        const isLow=item.quantity<=item.lowThreshold; const isZero=item.quantity===0;
                        const p=parsePos(item.position);
                        const isSelected = selectedItemId===item.id;
                        return (
                          <tr key={item.id} style={{
                            borderBottom:"1px solid rgba(255,100,100,0.04)", transition:"background 0.15s",
                            background: isSelected ? "rgba(212,68,59,0.08)" : "transparent",
                          }}
                            onMouseEnter={e=>{ if(!isSelected) e.currentTarget.style.background="rgba(255,255,255,0.02)"; }}
                            onMouseLeave={e=>{ if(!isSelected) e.currentTarget.style.background="transparent"; }}>
                            <td style={{ padding:"11px 12px", fontSize:13, fontWeight:500 }}>
                              <span onClick={()=>setSelectedItemId(isSelected?null:item.id)} style={{
                                cursor:"pointer", borderBottom:"1px dashed rgba(232,115,90,0.4)",
                                paddingBottom:1, transition:"color 0.2s",
                                color: isSelected ? "#E8735A" : "#F5E6E8",
                              }}
                                onMouseEnter={e=>e.target.style.color="#E8735A"}
                                onMouseLeave={e=>{ if(!isSelected) e.target.style.color="#F5E6E8"; }}
                              >{item.name}</span>
                            </td>
                            <td style={{ padding:"11px 12px" }}>
                              <span style={{ fontWeight:700, fontSize:14, fontFamily:mono, color:isZero?"#FF4444":isLow?"#E8735A":"#F5E6E8" }}>{item.quantity}</span>
                              {isZero&&<span style={{ marginLeft:5, fontSize:9, padding:"2px 5px", borderRadius:8, background:"rgba(255,68,68,0.2)", color:"#FF5544", fontWeight:700 }}>OUT</span>}
                              {isLow&&!isZero&&<span style={{ marginLeft:5, fontSize:9, padding:"2px 5px", borderRadius:8, background:"rgba(232,115,90,0.15)", color:"#E8735A", fontWeight:700 }}>LOW</span>}
                            </td>
                            <td style={{ padding:"11px 12px", fontSize:12, color:"#AA8888" }}>{item.unit}</td>
                            <td style={{ padding:"11px 12px" }}>
                              <span style={{ fontFamily:mono, fontSize:11, padding:"3px 7px", borderRadius:6, background:"rgba(212,68,59,0.1)", color:"#E8735A" }}>{item.position}</span>
                            </td>
                            <td style={{ padding:"11px 8px" }}>
                              <div style={{ display:"flex", gap:4 }}>
                                <button onClick={()=>openMove("out",item)} style={{ background:"rgba(184,58,51,0.15)", border:"none", color:"#E8735A", borderRadius:7, cursor:"pointer", fontSize:11, fontWeight:600, fontFamily:font, padding:"5px 10px", whiteSpace:"nowrap" }}>↑ Out</button>
                                <button onClick={()=>openMove("in",item)} style={{ background:"rgba(46,125,74,0.15)", border:"none", color:"#5AE89C", borderRadius:7, cursor:"pointer", fontSize:11, fontWeight:600, fontFamily:font, padding:"5px 10px", whiteSpace:"nowrap" }}>↓ In</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{ fontSize:11, color:"#664444", textAlign:"center", marginTop:6 }}>Showing {(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE, filteredItems.length)} of {filteredItems.length}{filteredItems.length!==items.length&&" (filtered from "+items.length+")"}</div>
                <Pager p={page} setP={setPage} total={filteredItems.length}/>
              </>
            )}

            {viewMode==="grid" && (
              <div>
                {Object.entries(groupedByShelter).sort(([a],[b])=>a.localeCompare(b)).map(([shelter, sItems]) => (
                  <div key={shelter} style={{ marginBottom:24 }}>
                    <h3 style={{ fontSize:14, fontWeight:700, color:"#E8735A", fontFamily:mono, marginBottom:10, padding:"8px 0", borderBottom:"1px solid rgba(255,100,100,0.08)" }}>
                      📍 Shelter {shelter} <span style={{ fontSize:11, color:"#775555", fontWeight:400, marginLeft:8 }}>{sItems.length} items</span>
                    </h3>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))", gap:10 }}>
                      {sItems.map(item => {
                        const isLow=item.quantity<=item.lowThreshold; const isZero=item.quantity===0; const p=parsePos(item.position);
                        const isSelected=selectedItemId===item.id;
                        return (
                          <div key={item.id} style={{
                            background: isSelected?"rgba(212,68,59,0.1)":"rgba(255,255,255,0.02)",
                            border:`1px solid ${isSelected?"rgba(212,68,59,0.3)":isZero?"rgba(255,68,68,0.2)":isLow?"rgba(232,115,90,0.15)":"rgba(255,100,100,0.05)"}`,
                            borderRadius:12, padding:"14px 16px",
                          }}>
                            <div style={{ fontSize:13, fontWeight:600, lineHeight:1.3, marginBottom:8 }}>
                              <span onClick={()=>setSelectedItemId(isSelected?null:item.id)} style={{ cursor:loggedIn?"pointer":"default", borderBottom:loggedIn?"1px dashed rgba(232,115,90,0.4)":"none", color:isSelected?"#E8735A":"#F5E6E8" }}>{item.name}</span>
                            </div>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                              <span style={{ fontFamily:mono, fontSize:20, fontWeight:700, color:isZero?"#FF4444":isLow?"#E8735A":"#F5E6E8" }}>
                                {item.quantity}<span style={{ fontSize:11, color:"#886666", fontWeight:400, marginLeft:4 }}>{item.unit}</span>
                              </span>
                              <span style={{ fontFamily:mono, fontSize:10, padding:"3px 7px", borderRadius:6, background:"rgba(212,68,59,0.1)", color:"#CC7766" }}>C{p.col} R{p.row}</span>
                            </div>
                            <div style={{ display:"flex", gap:4 }}>
                              <button onClick={()=>openMove("out",item)} style={{ flex:1, background:"rgba(184,58,51,0.12)", border:"none", color:"#E8735A", borderRadius:6, cursor:"pointer", fontSize:11, fontWeight:600, fontFamily:font, padding:"6px 0" }}>↑ Out</button>
                              <button onClick={()=>openMove("in",item)} style={{ flex:1, background:"rgba(46,125,74,0.12)", border:"none", color:"#5AE89C", borderRadius:6, cursor:"pointer", fontSize:11, fontWeight:600, fontFamily:font, padding:"6px 0" }}>↓ In</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: Item Detail Panel */}
          {loggedIn && liveSelectedItem && (
            <div style={{
              width: 340, minWidth: 300, marginLeft: 16,
              background:"rgba(255,255,255,0.02)", borderRadius:14,
              border:"1px solid rgba(255,100,100,0.08)", padding:20,
              position:"sticky", top:20, maxHeight:"calc(100vh - 280px)", overflowY:"auto",
            }}>
              {/* Close */}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}><CatBadge cat={liveSelectedItem.category}/><span style={{ fontSize:16, fontWeight:700, color:"#F5E6E8" }}>{liveSelectedItem.name}</span></div>
                  <div style={{ fontSize:12, color:"#886666", marginTop:4 }}>Position: <span style={{ fontFamily:mono, color:"#E8735A" }}>{liveSelectedItem.position}</span></div>
                </div>
                <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                  {!editMode && <button onClick={()=>{setEditMode(true);setSecurityPassed(false);setSecurityInput("");setSecurityError(false);}} style={{ background:"rgba(255,255,255,0.06)", border:"none", color:"#AA8888", width:28, height:28, borderRadius:7, cursor:"pointer", fontSize:12, display:"flex", alignItems:"center", justifyContent:"center" }}>✏️</button>}
                  <button onClick={()=>{setSelectedItemId(null);cancelInlineEdit();}} style={{ background:"rgba(255,255,255,0.06)", border:"none", color:"#AA8888", width:28, height:28, borderRadius:7, cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                </div>
              </div>

              {/* Inline Edit with Security Code */}
              {editMode && !securityPassed && (
                <div style={{ marginBottom:16, padding:14, background:"rgba(255,255,255,0.03)", borderRadius:10, border:"1px solid rgba(255,100,100,0.08)" }}>
                  <div style={{ fontSize:11, fontWeight:600, color:"#AA8888", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>🔒 Enter Security Code</div>
                  <div style={{ display:"flex", gap:6 }}>
                    <input
                      type="password" value={securityInput}
                      onChange={e=>{setSecurityInput(e.target.value);setSecurityError(false);}}
                      onKeyDown={e=>e.key==="Enter"&&handleSecuritySubmit()}
                      placeholder="Enter code..."
                      style={{ ...inputBase, flex:1, fontSize:16, letterSpacing:"0.2em", textAlign:"center" }}
                      autoFocus
                    />
                    <button onClick={handleSecuritySubmit} style={{ ...btnP, padding:"10px 16px", fontSize:12 }}>Verify</button>
                  </div>
                  {securityError && <div style={{ fontSize:11, color:"#FF5544", marginTop:6 }}>Incorrect code. Please try again.</div>}
                  <button onClick={cancelInlineEdit} style={{ fontSize:11, color:"#886666", background:"transparent", border:"none", cursor:"pointer", marginTop:6, fontFamily:font }}>Cancel</button>
                </div>
              )}

              {editMode && securityPassed && (
                <div style={{ marginBottom:16, padding:14, background:"rgba(46,125,74,0.06)", borderRadius:10, border:"1px solid rgba(46,125,74,0.15)" }}>
                  <div style={{ fontSize:11, fontWeight:600, color:"#5AE89C", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10 }}>✅ Edit Mode</div>
                  <div style={{ marginBottom:8 }}>
                    <div style={{ fontSize:10, color:"#886666", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.05em" }}>Item Name</div>
                    <input value={inlineEdit.name} onChange={e=>setInlineEdit({...inlineEdit, name:e.target.value})} style={inputBase} />
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
                    <div><div style={{ fontSize:10, color:"#886666", marginBottom:4, textTransform:"uppercase" }}>Quantity</div><input type="number" min="0" value={inlineEdit.quantity} onChange={e=>setInlineEdit({...inlineEdit, quantity:e.target.value})} style={inputBase}/></div>
                    <div><div style={{ fontSize:10, color:"#886666", marginBottom:4, textTransform:"uppercase" }}>Low Threshold</div><input type="number" min="0" value={inlineEdit.lowThreshold} onChange={e=>setInlineEdit({...inlineEdit, lowThreshold:e.target.value})} style={inputBase}/></div>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
                    <div>
                      <div style={{ fontSize:10, color:"#886666", marginBottom:4, textTransform:"uppercase" }}>Category</div>
                      <select value={inlineEdit.category} onChange={e=>setInlineEdit({...inlineEdit, category:e.target.value})} style={{...inputBase, cursor:"pointer"}}><option value="MG">MG</option><option value="IM">IM</option><option value="General">General</option></select>
                    </div>
                    <div>
                      <div style={{ fontSize:10, color:"#886666", marginBottom:4, textTransform:"uppercase" }}>Position Code</div>
                      <input value={inlineEdit.position} onChange={e=>setInlineEdit({...inlineEdit, position:e.target.value.replace(/[^0-9]/g,"")})} maxLength={6} placeholder="e.g. 010302" style={{...inputBase, fontFamily:mono}}/>
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:6 }}>
                    <button onClick={handleInlineSave} style={{ ...btnP, flex:1, background:"#2E7D4A", fontSize:12, padding:"9px 0" }}>Save Changes</button>
                    <button onClick={cancelInlineEdit} style={{ ...btnS, flex:1, fontSize:12, padding:"9px 0" }}>Cancel</button>
                  </div>
                  <button onClick={()=>{cancelInlineEdit();setDeleteConfirm(liveSelectedItem.id);}} style={{ width:"100%", marginTop:8, padding:"8px 0", borderRadius:8, border:"1px solid rgba(255,68,68,0.2)", background:"rgba(255,68,68,0.06)", color:"#FF5544", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:font }}>🗑 Delete This Item</button>
                </div>
              )}

              {/* Quick stats */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:18 }}>
                <div style={{ background:"rgba(255,255,255,0.03)", borderRadius:10, padding:"10px 12px" }}>
                  <div style={{ fontSize:10, color:"#775555", textTransform:"uppercase", letterSpacing:"0.06em" }}>Quantity</div>
                  <div style={{ fontSize:22, fontWeight:700, fontFamily:mono, color: liveSelectedItem.quantity===0?"#FF4444":liveSelectedItem.quantity<=liveSelectedItem.lowThreshold?"#E8735A":"#F5E6E8" }}>
                    {liveSelectedItem.quantity} <span style={{ fontSize:11, color:"#886666", fontWeight:400 }}>{liveSelectedItem.unit}</span>
                  </div>
                </div>
                <div style={{ background:"rgba(255,255,255,0.03)", borderRadius:10, padding:"10px 12px" }}>
                  <div style={{ fontSize:10, color:"#775555", textTransform:"uppercase", letterSpacing:"0.06em" }}>Movements</div>
                  <div style={{ fontSize:22, fontWeight:700, fontFamily:mono, color:"#CC9999" }}>{itemMovements.length}</div>
                </div>
              </div>

              {/* Quick move buttons */}
              <div style={{ display:"flex", gap:6, marginBottom:20 }}>
                <button onClick={()=>openMove("out",liveSelectedItem)} style={{ flex:1, background:"rgba(184,58,51,0.15)", border:"none", color:"#E8735A", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:600, fontFamily:font, padding:"8px 0" }}>↑ Take Out</button>
                <button onClick={()=>openMove("in",liveSelectedItem)} style={{ flex:1, background:"rgba(46,125,74,0.15)", border:"none", color:"#5AE89C", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:600, fontFamily:font, padding:"8px 0" }}>↓ Stock In</button>
              </div>

              {/* Position Map */}
              <div style={{ marginBottom:20, padding:14, background:"rgba(0,0,0,0.2)", borderRadius:12, border:"1px solid rgba(255,100,100,0.06)" }}>
                <ShelterMap
                  shelterCode={selectedPos.shelter}
                  highlightCol={selectedPos.col}
                  highlightRow={selectedPos.row}
                  items={items}
                />
              </div>

              {/* Item Transaction History */}
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:"#AA8888", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10 }}>
                  Transaction History
                </div>
                {itemMovements.length===0 ? (
                  <div style={{ textAlign:"center", padding:24, color:"#553333", fontSize:12 }}>
                    No transactions recorded yet
                  </div>
                ) : (
                  <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                    {itemMovements.slice(0,5).map(m => (
                      <div key={m.id} style={{
                        background:"rgba(255,255,255,0.02)", borderRadius:8, padding:"10px 12px",
                        border:"1px solid rgba(255,100,100,0.04)",
                      }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                          <span style={{
                            fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:5,
                            background: m.type==="out"?"rgba(184,58,51,0.15)":"rgba(46,125,74,0.15)",
                            color: m.type==="out"?"#E8735A":"#5AE89C",
                          }}>{m.type==="out"?"↑ OUT":"↓ IN"}</span>
                          <span style={{ fontSize:10, color:"#664444" }}>{m.datetime}</span>
                        </div>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                          <div>
                            <span style={{ fontFamily:mono, fontSize:14, fontWeight:700, color:m.type==="out"?"#E8735A":"#5AE89C" }}>
                              {m.type==="out"?"−":"+"}{m.qty}
                            </span>
                            <span style={{ fontSize:12, color:"#886666", marginLeft:6 }}>by {m.person}</span>
                          </div>
                          <span style={{ fontFamily:mono, fontSize:11, color:"#886666" }}>{m.balanceBefore}→{m.balanceAfter}</span>
                        </div>
                        {m.purpose!=="–" && (
                          <div style={{ fontSize:11, color:"#775555", marginTop:4, fontStyle:"italic" }}>{m.purpose}</div>
                        )}
                      </div>
                    ))}
                    {itemMovements.length>5 && <div style={{ textAlign:"center", fontSize:10, color:"#664444", padding:"8px 0" }}>Showing latest 5 of {itemMovements.length} transactions</div>}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </>)}

      {/* ═══ MOVEMENTS TAB ═══ */}
      {tab==="movements" && loggedIn && (
        <div style={{ padding:"20px 28px 32px" }}>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", marginBottom:16 }}>
            <div style={{ position:"relative", flex:"1 1 200px", maxWidth:300 }}>
              <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color:"#664444", fontSize:14 }}>🔍</span>
              <input placeholder="Search item, person, purpose..." value={moveSearch} onChange={e=>setMoveSearch(e.target.value)} style={{ ...inputBase, paddingLeft:34 }} />
            </div>
            <select value={moveHistoryFilter} onChange={e=>setMoveHistoryFilter(e.target.value)} style={{ ...inputBase, width:"auto", minWidth:130, cursor:"pointer" }}>
              <option value="all">All Movements</option>
              <option value="out">↑ Stock Out</option>
              <option value="in">↓ Stock In</option>
            </select>
            <button onClick={exportMovements} style={{ ...btnS, padding:"10px 14px", fontSize:13 }}>↓ Export CSV</button>

          </div>
          {filteredMovements.length===0 ? (
            <div style={{ textAlign:"center", padding:60, color:"#664444" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🔄</div>
              <div style={{ fontSize:14 }}>No stock movements yet</div>
              <div style={{ fontSize:12, marginTop:4 }}>Use "Take Out" or "Stock In" to record movements</div>
            </div>
          ) : (
            <div style={{ background:"rgba(255,255,255,0.015)", borderRadius:14, border:"1px solid rgba(255,100,100,0.06)", overflow:"hidden", overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:700 }}>
                <thead>
                  <tr style={{ borderBottom:"1px solid rgba(255,100,100,0.08)" }}>
                    {["Date / Time","Type","Item","Qty","Person","Purpose","Balance"].map((h,i)=>(
                      <th key={i} style={{ textAlign:"left", padding:"12px 14px", fontSize:10, fontWeight:600, color:"#775555", textTransform:"uppercase", letterSpacing:"0.08em" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedMv.map(m=>(
                    <tr key={m.id} style={{ borderBottom:"1px solid rgba(255,100,100,0.04)" }}
                      onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.02)"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{ padding:"11px 14px", fontSize:12, color:"#AA8888", whiteSpace:"nowrap" }}>{m.datetime}</td>
                      <td style={{ padding:"11px 14px" }}>
                        <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:20, fontSize:11, fontWeight:600, background:m.type==="out"?"rgba(184,58,51,0.15)":"rgba(46,125,74,0.15)", color:m.type==="out"?"#E8735A":"#5AE89C" }}>{m.type==="out"?"↑ OUT":"↓ IN"}</span>
                      </td>
                      <td style={{ padding:"11px 14px", fontSize:13, fontWeight:500 }}>{m.itemName}</td>
                      <td style={{ padding:"11px 14px", fontFamily:mono, fontSize:14, fontWeight:700, color:m.type==="out"?"#E8735A":"#5AE89C" }}>{m.type==="out"?"−":"+"}{m.qty}</td>
                      <td style={{ padding:"11px 14px", fontSize:13, color:"#CC9999" }}>{m.person}</td>
                      <td style={{ padding:"11px 14px", fontSize:12, color:"#886666", maxWidth:150, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.purpose}</td>
                      <td style={{ padding:"11px 14px", fontSize:12, fontFamily:mono, color:"#AA8888" }}>{m.balanceBefore} → <span style={{ fontWeight:700, color:"#F5E6E8" }}>{m.balanceAfter}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize:11, color:"#664444", textAlign:"center", marginTop:6 }}>{filteredMovements.length} movements</div>
          <Pager p={mvPage} setP={setMvPage} total={filteredMovements.length}/>
        </div>
      )}

      {/* ═══ HISTORY TAB ═══ */}
      {tab==="history" && loggedIn && (
        <div style={{ padding:"20px 28px 32px" }}>
          {history.length===0 ? (
            <div style={{ textAlign:"center", padding:60, color:"#664444" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>📜</div>
              <div style={{ fontSize:14 }}>No changes recorded yet</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {paginatedHist.map(h => {
                const colors = { "Added":"#5AE89C","Edited":"#5A8FE8","Deleted":"#FF5544","Stock Out":"#E8735A","Stock In":"#5AE89C" };
                return (
                  <div key={h.id} style={{ background:"rgba(255,255,255,0.02)", borderRadius:10, padding:"12px 16px", border:"1px solid rgba(255,100,100,0.05)", display:"flex", gap:12, alignItems:"flex-start" }}>
                    <span style={{ fontSize:10, fontWeight:700, padding:"3px 8px", borderRadius:6, whiteSpace:"nowrap", marginTop:2, background:`${colors[h.action]||"#CC9999"}18`, color:colors[h.action]||"#CC9999" }}>{h.action}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:"#F5E6E8" }}>{h.itemName}</div>
                      <div style={{ fontSize:12, color:"#886666", marginTop:2 }}>{h.details}</div>
                    </div>
                    <div style={{ fontSize:11, color:"#664444", whiteSpace:"nowrap" }}>{h.datetime}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Overview Tab */}
      {tab==="overview" && loggedIn && (
        <div style={{ padding:"20px 28px 32px" }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(130px, 1fr))", gap:10, marginBottom:24 }}>
            {[{l:"All Items",v:overview.length,a:"#E8735A",f:"all"},{l:"MG Items",v:overview.filter(i=>i.category==="MG").length,a:"#E85A5A",f:"MG"},{l:"IM Items",v:overview.filter(i=>i.category==="IM").length,a:"#5A8FE8",f:"IM"},{l:"General",v:overview.filter(i=>i.category==="General").length,a:"#CC9999",f:"General"},{l:"Total Stock",v:overview.reduce((s,i)=>s+i.totalQty,0).toLocaleString(),a:"#D4886A",f:null}].map((s,i)=>(
              <div key={i} onClick={()=>{if(s.f!==null){setOverviewCatFilter(overviewCatFilter===s.f?"all":s.f);setOvPage(1);}}} style={{ background:overviewCatFilter===s.f?"rgba(255,255,255,0.06)":"rgba(255,255,255,0.025)", borderRadius:12, padding:"14px 16px", border:overviewCatFilter===s.f?"1px solid rgba(255,100,100,0.2)":"1px solid rgba(255,100,100,0.06)", cursor:s.f!==null?"pointer":"default", transition:"all 0.2s" }}>
                <div style={{ fontSize:10, color:"#885555", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>{s.l}{overviewCatFilter===s.f&&s.f!=="all"&&" ✓"}</div>
                <div style={{ fontSize:24, fontWeight:700, color:s.a, fontFamily:mono }}>{s.v}</div>
              </div>))}
          </div>
          <div style={{ marginBottom:14, position:"relative" }}>
            <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color:"#664444", fontSize:14 }}>🔍</span>
            <input value={overviewSearch} onChange={e=>setOverviewSearch(e.target.value)} placeholder="Search items..." style={{ ...inputBase, paddingLeft:34, fontSize:13 }}/>
          </div>
          <div style={{ background:"rgba(255,255,255,0.015)", borderRadius:14, border:"1px solid rgba(255,100,100,0.06)", overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead style={{ position:"sticky", top:0, zIndex:2, background:"#2A1014" }}><tr style={{ borderBottom:"1px solid rgba(255,100,100,0.08)" }}>
                {["Item Name","Cat","Total Qty","Unit","Locations","Threshold"].map((h,i)=><th key={i} style={{ textAlign:"left", padding:"12px 10px", fontSize:10, fontWeight:600, color:"#775555", textTransform:"uppercase", letterSpacing:"0.08em" }}>{h}</th>)}
              </tr></thead>
              <tbody>{paginatedOv.map((item,idx)=>{const isLow=item.totalQty<=item.lowThreshold;const isZero=item.totalQty===0;
                return <tr key={idx} style={{ borderBottom:"1px solid rgba(255,100,100,0.04)" }}>
                  <td style={{ padding:"11px 10px", fontSize:13, fontWeight:500, color:"#F5E6E8" }}>{item.name}</td>
                  <td style={{ padding:"11px 6px" }}><CatBadge cat={item.category}/></td>
                  <td style={{ padding:"11px 10px" }}><span style={{ fontWeight:700, fontSize:14, fontFamily:mono, color:isZero?"#FF4444":isLow?"#E8735A":"#F5E6E8" }}>{item.totalQty}</span>{isLow&&<span style={{ marginLeft:4, fontSize:9, padding:"2px 5px", borderRadius:8, background:"rgba(232,115,90,0.15)", color:"#E8735A", fontWeight:700 }}>{isZero?"OUT":"LOW"}</span>}</td>
                  <td style={{ padding:"11px 6px", fontSize:12, color:"#AA8888" }}>{item.unit}</td>
                  <td style={{ padding:"11px 6px" }}><div style={{ display:"flex", gap:3, flexWrap:"wrap" }}>{item.positions.map((p,j)=><span key={j} style={{ fontFamily:mono, fontSize:10, padding:"2px 6px", borderRadius:5, background:"rgba(212,68,59,0.1)", color:"#CC7766" }}>{p}</span>)}</div></td>
                  <td style={{ padding:"11px 6px", fontFamily:mono, fontSize:12, color:"#886666" }}>{item.lowThreshold}</td>
                </tr>})}</tbody>
            </table>
          </div>
          <div style={{ fontSize:11, color:"#664444", textAlign:"center", marginTop:6 }}>{filteredOverview.length} items</div>
          <Pager p={ovPage} setP={setOvPage} total={filteredOverview.length}/>
        </div>
      )}

      {/* Login Modal */}
      <Modal isOpen={showLogin} onClose={()=>{setShowLogin(false);setLoginError(false);}} title="Admin Login">
        <div style={{marginBottom:16}}><label style={label}>Email</label><input type="email" value={loginEmail} onChange={e=>{setLoginEmail(e.target.value);setLoginError(false);}} placeholder="marketing@..." style={inputBase}/></div>
        <div style={{marginBottom:16}}><label style={label}>Password</label><input type="password" value={loginPass} onChange={e=>{setLoginPass(e.target.value);setLoginError(false);}} onKeyDown={e=>e.key==="Enter"&&handleLogin()} placeholder="Enter password..." style={inputBase}/></div>
        {loginError&&<div style={{fontSize:12,color:"#FF5544",marginBottom:12}}>Invalid email or password.</div>}
        <button onClick={handleLogin} style={{...btnP,width:"100%"}}>Log In</button>
      </Modal>

      {/* ═══ Stock Movement Modal ═══ */}
      <Modal isOpen={moveModal} onClose={()=>setMoveModal(false)} title={moveType==="out"?"↑ Take Out Stock":"↓ Stock In"}>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div>
            <label style={label}>Select Item *</label>
            <select value={moveForm.itemId} onChange={e=>setMoveForm({...moveForm,itemId:e.target.value})} style={{ ...inputBase, cursor:"pointer" }}>
              <option value="">— Choose item —</option>
              {[...items].sort((a,b)=>a.name.localeCompare(b.name)).map(i=>(
                <option key={i.id} value={i.id}>{i.name} ({i.position}) — {i.quantity} {i.unit}</option>
              ))}
            </select>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div>
              <label style={label}>Quantity *</label>
              <input type="number" min="1" max={moveType==="out"&&moveForm.itemId?items.find(i=>i.id===moveForm.itemId)?.quantity:undefined}
                value={moveForm.qty} onChange={e=>setMoveForm({...moveForm,qty:e.target.value})} placeholder="0" style={inputBase} />
              {moveType==="out"&&moveForm.itemId&&(
                <div style={{ fontSize:10, color:"#886666", marginTop:4 }}>Available: {items.find(i=>i.id===moveForm.itemId)?.quantity||0}</div>
              )}
            </div>
            <div>
              <label style={label}>By Whom *</label>
              <input value={moveForm.person} onChange={e=>setMoveForm({...moveForm,person:e.target.value})} placeholder="Name" style={inputBase} />
            </div>
          </div>
          <div>
            <label style={label}>Purpose <span style={{ fontWeight:400, textTransform:"none" }}>(optional)</span></label>
            <input value={moveForm.purpose} onChange={e=>setMoveForm({...moveForm,purpose:e.target.value})} placeholder="e.g. Roadshow event, Client gift..." style={inputBase} />
          </div>
          <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:6 }}>
            <button onClick={()=>setMoveModal(false)} style={btnS}>Cancel</button>
            {moveError && <div style={{color:"#FF5544",fontSize:12,marginBottom:8}}>{moveError}</div>}
            <button onClick={handleMove} style={{ ...btnP, background:moveType==="out"?"#B83A33":"#2E7D4A", opacity:(!moveForm.itemId||!moveForm.qty||!moveForm.person.trim())?0.4:1 }}>
              {moveType==="out"?"↑ Confirm Take Out":"↓ Confirm Stock In"}
            </button>
          </div>
        </div>
      </Modal>

      </div>{/* end maxWidth */}

      {/* Add/Edit Modal */}
      <Modal isOpen={modalOpen} onClose={()=>{setModalOpen(false);setEditingItem(null);}} title={editingItem?"Edit Item":"Add New Item"}>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div><label style={label}>Item Name *</label><input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="e.g. MG Canvas Bag" style={inputBase}/></div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div><label style={label}>Quantity *</label><input type="number" min="0" value={form.quantity} onChange={e=>setForm({...form,quantity:e.target.value})} placeholder="0" style={inputBase}/></div>
            <div><label style={label}>Unit</label><select value={form.unit} onChange={e=>setForm({...form,unit:e.target.value})} style={{...inputBase,cursor:"pointer"}}>{["pcs","box","set","dozens","sheet","batch","rolls","packs"].map(u=><option key={u} value={u}>{u}</option>)}</select></div>
          </div>
          <div><label style={label}>Position Code</label><input value={form.position} onChange={e=>setForm({...form,position:e.target.value})} placeholder="e.g. 010302" style={inputBase} maxLength={6}/><div style={{fontSize:10,color:"#664444",marginTop:4}}>Shelter(2) + Column(2) + Row(2)</div></div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
              <div><label style={label}>Category</label><select value={form.category||"General"} onChange={e=>setForm({...form, category:e.target.value})} style={{...inputBase, cursor:"pointer"}}><option value="MG">MG</option><option value="IM">IM</option><option value="General">General</option></select></div>
              <div><label style={label}>Low Threshold</label><input type="number" min="0" value={form.lowThreshold||DEFAULT_LOW} onChange={e=>setForm({...form, lowThreshold:e.target.value})} style={inputBase}/></div>
            </div>
          <div><label style={label}>Notes</label><textarea value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Additional details..." rows={2} style={{...inputBase,resize:"vertical"}}/></div>
          <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:6 }}>
            <button onClick={()=>{setModalOpen(false);setEditingItem(null);}} style={btnS}>Cancel</button>
            <button onClick={handleSave} style={{...btnP,opacity:(!form.name.trim()||form.quantity==="")?0.4:1}}>{editingItem?"Save Changes":"Add Item"}</button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={!!deleteConfirm} onClose={()=>setDeleteConfirm(null)} title="Confirm Delete">
        <p style={{ color:"#AA8888", fontSize:14, marginBottom:24 }}>Are you sure? This cannot be undone.</p>
        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
          <button onClick={()=>setDeleteConfirm(null)} style={btnS}>Cancel</button>
          <button onClick={()=>handleDelete(deleteConfirm)} style={btnP}>Delete</button>
        </div>
      </Modal>
    </div>
  </>);
}